import React, { useState } from "react";
import { Copy, Layers } from "lucide-react";
import { toast } from "sonner";
import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";

const BASE = resolveBackendUrl() || "https://your-aiservice-host.example";

const UPLOAD_CURL = `# 1. Send NEW files — protocol + budget required, CTA optional (multipart/form-data)
curl -X POST "${BASE}/api/v1/study-builds/upload" \\
  -H "Authorization: Bearer $CLIENT_TOKEN" \\
  -F "protocol_file=@SOL-ADHD-301_protocol_amend2_v3.pdf" \\
  -F "budget_file=@SOL-ADHD-301_payment_schedule_amd4.xlsx" \\
  -F "cta_file=@SOL-ADHD-301_CTA.docx" \\
  -F "site_id=207" \\
  -F "matching=ai" \\
  -F "webhook_url=https://sitecentric.example/hooks/aiservice"

# Mix: new protocol file + budget already in the Data Lake
curl -X POST "${BASE}/api/v1/study-builds/upload" \\
  -H "Authorization: Bearer $CLIENT_TOKEN" \\
  -F "protocol_file=@protocol.pdf" \\
  -F "budget_id=dc72b9aa-9840-4062-bcce-f3b43b48fdad"

# Optional: force_reextract=true re-runs the budget / CTA extraction even if the
# identical file was ingested before (use after a bad extraction).`;

const JSON_CURL = `# 1b. Reference documents already stored (ingest ids or Data Lake item ids)
curl -X POST "${BASE}/api/v1/study-builds" \\
  -H "Authorization: Bearer $CLIENT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "protocol_id": "d1bb0fb6-1676-4707-b3ec-467000559a36",
    "budget_id":   "dc72b9aa-9840-4062-bcce-f3b43b48fdad",
    "cta_id":      "94ddaf87-b89a-4e70-ad87-0a64ed637ce0",
    "site_id":     "207",
    "matching":    "ai",
    "webhook_url": "https://sitecentric.example/hooks/aiservice"
  }'`;

const ACCEPTED_JSON = `HTTP/1.1 202 Accepted
{
  "id": "9698ae46-9dae-4ffe-8b03-35df2a4171a7",
  "status": "queued",
  "client_id": "sitecentric",
  "poll_url": "/api/v1/study-builds/9698ae46-9dae-4ffe-8b03-35df2a4171a7",
  "progress": {
    "percent": 5,
    "stages": { "protocol": "queued", "budget": "queued", "cta": "queued", "compose": "pending" },
    "terminal": false
  },
  "request": {
    "sources": {
      "protocol": { "mode": "upload", "filename": "SOL-ADHD-301_protocol_amend2_v3.pdf", "size_bytes": 2264712,
                    "content_type": "application/pdf", "job_id": "179fcfc5-…", "poll_url": "/api/v1/ingest-jobs/179fcfc5-…" },
      "budget":   { "mode": "upload", "filename": "SOL-ADHD-301_payment_schedule_amd4.xlsx", "size_bytes": 126746,
                    "job_id": "b49d5963-…", "poll_url": "/api/v1/ingest-jobs/b49d5963-…" },
      "cta":      { "mode": "existing", "ref": "94ddaf87-…", "id": "94ddaf87-…", "resolved_from": "cta",
                    "filename": "SOL-ADHD-301_CTA.docx", "status": "ready" }
    },
    "site_id": "207", "protocol_meta": null, "webhook_url": "https://sitecentric.example/hooks/aiservice",
    "matching": "ai", "force_reextract": false,
    "submitted_at": "2026-06-18T11:43:02+00:00", "submitted_by": "api-key:tf_live_…9f3a", "origin": "api"
  },
  "steps": {
    "protocol": { "status": "queued", "id": null, "job_id": "179fcfc5-…", "error": null },
    "budget":   { "status": "queued", "id": null, "job_id": "b49d5963-…", "error": null },
    "cta":      { "status": "existing", "id": "94ddaf87-…", "job_id": null, "error": null },
    "compose":  { "status": "pending", "error": null }
  },
  "created_at": "2026-06-18T11:43:02+00:00",
  "cost": { "total_cost": 0.0, "call_detail": "Study Build: 9698ae46", "call_timestamp": "2026-06-18 11:43" }   // nothing spent yet
}

# 2. Poll until progress.terminal is true (status: queued → ingesting → composing → ready | failed)
curl "${BASE}/api/v1/study-builds/9698ae46-9dae-4ffe-8b03-35df2a4171a7" \\
  -H "Authorization: Bearer $CLIENT_TOKEN"
# → while running: { "status": "composing", "progress": { "percent": 75, "stages": { "protocol": "ready", "budget": "ready", "cta": "ready", "compose": "running" }, "terminal": false }, … }`;

const RESULT_JSON = `HTTP/1.1 200 OK            # status: ready — full build; visits[] trimmed here, see each endpoint's example for more
{
  "id": "9698ae46-9dae-4ffe-8b03-35df2a4171a7",
  "status": "ready",
  "client_id": "sitecentric",
  "poll_url": "/api/v1/study-builds/9698ae46-9dae-4ffe-8b03-35df2a4171a7",
  "progress": { "percent": 100, "stages": { "protocol": "ready", "budget": "ready", "cta": "ready", "compose": "done" }, "terminal": true },
  "request":  { "...": "echo of the request, as in the 202 response" },
  "steps":    { "protocol": { "status": "ready", "id": "d1bb0fb6-…" }, "budget": { "status": "ready", "id": "dc72b9aa-…" },
                "cta": { "status": "ready", "id": "94ddaf87-…" }, "compose": { "status": "done", "error": null } },
  "protocol_id": "d1bb0fb6-1676-4707-b3ec-467000559a36",
  "budget_id":   "dc72b9aa-9840-4062-bcce-f3b43b48fdad",
  "cta_id":      "94ddaf87-b89a-4e70-ad87-0a64ed637ce0",
  "summary": {
    "protocol_number": "SOL-ADHD-301",
    "title": "A Phase 3, Randomized, Double-blind, Placebo-controlled Trial of Solriamfetol in Adults with ADHD",
    "sponsor": "Axsome Therapeutics, Inc.",
    "visits": 9, "protocol_visits": 9, "budget_visits": 9,
    "totals": { "visits_total_usd": 15228.22, "overhead_total_usd": 2354.22, "ancillary_total_usd": 3840.0, "additional_procedures_total_usd": 355.0,
                "start_up_total_usd": 12567.0, "grand_total_usd": 28150.22 },
    "discrepancies": { "total": 20, "high": 1, "medium": 10, "low": 0, "info": 9 },
    "matching": "ai"
  },
  "discrepancy_count": 20,
  "build": {
    "schema_version": "study_build.v2",
    "study": {
      "protocol_number": "SOL-ADHD-301", "version": "Amendment 2 (V3.0)", "phase": "Phase 3",
      "title": "A Phase 3, Randomized, Double-blind, Placebo-controlled Trial of Solriamfetol in Adults with ADHD",
      "indication": "Attention deficit hyperactivity disorder", "sponsor": "Axsome Therapeutics, Inc.",
      "principal_investigator": null, "site": null, "site_id": "207", "currency": "USD"
    },
    "schedule": {
      "source": "protocol.visits", "visit_count": 9, "procedure_count": 38,
      "anchor_visit": {                         // the visit study days are counted from (usually Baseline / Randomization / Day 1)
        "label": "V2", "name": "Baseline", "visit_number": 2, "day_offset_from_baseline": 0, "relation": "self",
        "timing_label": "Baseline (Day 1)", "day_convention": "day_0", "source": "protocol",
        "visits_anchored_elsewhere": [ { "visit": "Week 7 (Safety Follow-up)", "anchored_to": "Week 6 (ET/EOS)", "offset_days": 7 } ]
      },
      "cohorts": [
        { "name": "Solriamfetol 150 mg", "intervention": "Solriamfetol 75 mg Days 1-3, then 150 mg once daily from Day 4, oral", "target_n": null },
        { "name": "Solriamfetol 300 mg", "intervention": "Solriamfetol 75 mg Days 1-3, 150 mg Days 4-7, then 300 mg once daily from Day 8, oral", "target_n": null },
        { "name": "Placebo", "intervention": "Matching placebo once daily, oral", "target_n": null }
      ],
      "cohort_specific_visits": []          // visits that exist only for some arms are listed here and carry "cohorts": [...]
    },
    "budget_summary": {
      "visit_columns": ["Visit 1", "Visit 2", "Visit 3/ Remote", "Visit 4", "Visit 5", "Visit 6", "Visit 7", "Visit 8", "Visit 9/ Remote"],
      "line_items": 26, "per_patient_total_declared_usd": null, "budget_version": "Amendment 3", "protocol_version": "Amendment 2"
    },
    "matching": { "method": "ai", "model": "claude-sonnet-5-5", "duration_ms": 20041, "error": null,
                  "visit_pairs": 9, "procedure_pairs": 26, "budget_lines": 26, "protocol_procedures": 38,
                  "ai_notes": ["BRIEF-A is paid at Visits 2, 6 and 8, but the protocol schedules it at Baseline, Week 4 (Visit 7) and Week 6 …"] },
    "visits": [
      {
        "label": "V1", "ordinal": 1, "name": "Screening", "visit_number": 1, "sequence": 1,
        "day_offset_from_baseline": -35, "timing_label": "Screening (Days -35 to -1)",
        "window_before_days": 0, "window_after_days": 0,
        "window": { "before_days": 0, "after_days": 0, "label": "Days -35 to -1", "earliest_day": -35, "latest_day": -35 },
        "is_anchor": false,
        "anchor": { "label": "V2", "name": "Baseline", "visit_number": 2, "day_offset_from_baseline": 0, "relation": "baseline" },   // relation: self · baseline · visit
        "offset_days_from_anchor": -35,
        "location": "Clinic", "cohorts": [],
        "protocol_label": "Screening", "budget_label": "Visit 1", "visit_match_confidence": 0.97, "matched_from": "protocol+budget",
        "procedures": [
          { "label": "Informed consent", "protocol_procedure": "Informed consent", "category": "consent",
            "budget_code": "PRC020", "budget_line": "Informed consent", "unit_cost_usd": 150.0, "quantity": 1, "total_usd": 150.0,
            "marker": null, "matched_from": "protocol+budget", "match_method": "ai", "confidence": 0.95 },
          { "label": "Height", "protocol_procedure": "Height", "category": "vitals",
            "budget_code": null, "budget_line": null, "unit_cost_usd": null, "quantity": 1, "total_usd": null,
            "marker": null, "matched_from": "protocol", "match_method": null, "confidence": null },
          { "label": "ECG", "protocol_procedure": "ECG", "category": "ecg",
            "budget_code": "PRC025", "budget_line": "ECG", "unit_cost_usd": 136.0, "quantity": 1, "total_usd": 136.0,
            "marker": null, "matched_from": "protocol+budget", "match_method": "ai", "confidence": 0.98 }
          // … 22 more procedures at this visit, in Schedule-of-Events order
        ],
        "ancillary": [
          { "code": "ANC048", "label": "Overhead 33% (procedures only)", "amount_usd": 514.8, "unit": "per_visit" },
          { "code": "ANC050", "label": "Physician's Fees without Exam Costs", "amount_usd": 225.0, "unit": "per_visit" },
          { "code": "ANC051", "label": "Study Coordinator Fee Per Visit", "amount_usd": 167.0, "unit": "per_visit" },
          { "code": "ANC052", "label": "Patient Reimbursement", "amount_usd": 200.0, "unit": "per_visit" }
        ],
        "additional_procedures": [],
        "procedures_total_usd": 2010.0, "ancillary_total_usd": 1106.8, "visit_total_usd": 3116.8
      },
      { "label": "V2", "ordinal": 2, "name": "Baseline", "visit_number": 2, "timing_label": "Baseline (Day 1)", "budget_label": "Visit 2",
        "is_anchor": true, "anchor": { "label": "V2", "name": "Baseline", "visit_number": 2, "day_offset_from_baseline": 0, "relation": "self" }, "offset_days_from_anchor": 0,
        "window": { "before_days": 0, "after_days": 0, "label": null, "earliest_day": 0, "latest_day": 0 },
        "visit_match_confidence": 0.97, "procedures": ["…21 procedures"], "visit_total_usd": 2128.45 },
      { "label": "V3", "ordinal": 3, "name": "Day 4", "visit_number": 3, "timing_label": "Day 4 (±1)", "location": "Remote",
        "window": { "before_days": 1, "after_days": 1, "label": "± 1 day", "earliest_day": 2, "latest_day": 4 },
        "anchor": { "label": "V2", "name": "Baseline", "visit_number": 2, "day_offset_from_baseline": 0, "relation": "baseline" }, "offset_days_from_anchor": 3,
        "budget_label": "Visit 3/ Remote", "visit_match_confidence": 0.9, "procedures": ["…5 procedures"], "visit_total_usd": 355.75 },
      { "...": "Week 1 · Week 2 · Week 3 · Week 4 · Week 6 (ET/EOS) · Week 7 — 9 visits total, protocol order" }
    ],
    "discrepancies": [
      { "id": "d1", "type": "sponsor_mismatch", "severity": "high",
        "message": "The CTA names sponsor 'Suven Life Sciences Ltd.' but the protocol sponsor is 'Axsome Therapeutics, Inc.' — the payment terms and milestones below may belong to a different study.",
        "protocol_says": "Axsome Therapeutics, Inc.", "cta_says": "Suven Life Sciences Ltd." },
      { "id": "d2", "type": "procedure_unpaid_at_visit", "severity": "medium",
        "message": "'Vital signs' / 'Weight' are scheduled at Screening but the budget line 'Vital signs, including weight' has no amount in column 'Visit 1'.",
        "visit": "Screening", "procedure": "Vital signs", "procedures": ["Vital signs", "Weight"],
        "protocol_says": "performed", "budget_says": "blank", "budget_code": "PRC021" },
      { "id": "d3", "type": "procedure_not_in_protocol_at_visit", "severity": "medium",
        "message": "The budget pays 'BRIEF-A' at 'Visit 6' (Week 3) but the protocol schedule does not list 'BRIEF-A' at that visit.",
        "visit": "Week 3", "procedure": "BRIEF-A", "budget_code": "PRC031", "protocol_says": "not scheduled", "budget_says": "$45.00" },
      { "id": "d4", "type": "procedure_not_priced", "severity": "medium",
        "message": "'Fasting status collection' appears in the protocol schedule at 7 visit(s) but no budget line pays for it.",
        "procedure": "Fasting status collection", "visits": ["Screening", "Baseline", "Week 1", "Week 2", "Week 3", "Week 4", "Week 6 (ET/EOS)"],
        "protocol_says": "scheduled", "budget_says": "no line item" },
      { "id": "d5", "type": "reviewer_note", "severity": "info",
        "message": "Urine pregnancy test has no amounts at any visit, although the protocol schedules it at Screening, Baseline, Week 3 and Week 6." }
      // … types also include study_mismatch · visit_not_in_budget · visit_not_in_protocol · visit_count_mismatch ·
      //   budget_line_not_in_protocol · uncertain_visit_match · total_mismatch
    ],
    "discrepancy_summary": { "total": 20, "high": 1, "medium": 10, "low": 0, "info": 9,
      "by_type": { "sponsor_mismatch": 1, "procedure_unpaid_at_visit": 2, "procedure_not_in_protocol_at_visit": 2, "procedure_not_priced": 6, "reviewer_note": 9 } },
    "study_level_additional_procedures": [
      { "code": "INV063", "procedure": "Re-consent (per re-consent)", "invoiceable": true, "unit_cost": 75.0 },
      { "code": "INV064", "procedure": "Urine Pregnancy test (for WOCBP), per test", "invoiceable": true, "unit_cost": 30.0 }
    ],
    "ancillary_summary": [
      { "code": "ANC048", "label": "Overhead 33% (procedures only)", "total_usd": 2354.22, "unit": "per_visit" },
      { "code": "ANC050", "label": "Physician's Fees without Exam Costs", "total_usd": 1687.5, "unit": "per_visit" }
    ],
    "start_up_costs": [
      { "code": "SU060", "description": "Administrative/Site Start Up Costs", "amount_usd": 4000.0, "one_time": true, "invoice_trigger": null },
      { "code": "SU061", "description": "Screen Failure at Visit 1, as defined in Clinical Trial Agreement", "amount_usd": 2133.44, "one_time": false, "invoice_trigger": null }
    ],
    "totals": { "visits_total_usd": 15228.22, "overhead_total_usd": 2354.22, "ancillary_total_usd": 3840.0, "additional_procedures_total_usd": 355.0,
                "start_up_total_usd": 12567.0, "grand_total_usd": 28150.22 },
    "alignment": { "matched_procedures": 115, "unmatched_protocol_procedures": [{ "visit": "V1", "procedure": "Height" }],
                   "unmatched_budget_procedures": [], "warnings": ["'Height' appears in the protocol schedule at 1 visit(s) but no budget line pays for it."] },
    "payment_terms": { "net_payment_days": 45, "holdback_percent": null,
                       "invoice_cycle": "Per-visit payments based on CRF data entered for completed visits per Attachment B. …",
                       "currency": "USD", "source": "cta" },
    "milestones": []
  },
  "discrepancies": ["… same rows as build.discrepancies (shortcut)"],
  "error": null,
  "created_at": "2026-06-18T11:43:02+00:00", "updated_at": "2026-06-18T11:47:10+00:00", "finished_at": "2026-06-18T11:47:10+00:00",
  "cost": { "total_cost": 0.2184, "call_detail": "Study Build: Axsome Therapeutics, Inc. SOL-ADHD-301", "call_timestamp": "2026-06-18 11:47" }
}

# failed build → { "status": "failed", "error": "Protocol extraction failed: …", "steps": { "protocol": { "status": "failed", "error": "…" } }, "build": null,
#                  "cost": { "total_cost": 0.0312, "call_detail": "Study Build: Axsome Therapeutics, Inc. SOL-ADHD-301", "call_timestamp": "2026-06-18 11:44" } }

# Kill / retry
curl -X POST "${BASE}/api/v1/study-builds/<build_id>/cancel" -H "Authorization: Bearer $CLIENT_TOKEN"
# → { "status": "cancelled", "error": "Cancelled by …", "progress": { "percent": 100, "terminal": true },
#     "cost": { "total_cost": 0.0, "call_detail": "Study Build: Axsome Therapeutics, Inc. SOL-ADHD-301 (cancel)", "call_timestamp": "2026-06-18 11:45" } }
curl -X POST "${BASE}/api/v1/study-builds/<build_id>/retry" -H "Authorization: Bearer $CLIENT_TOKEN" \\
  -H "Content-Type: application/json" -d '{"force_reextract": false}'
# → same id back to "queued"; finished documents are reused, failed ingests re-run from the stored upload;
#   force_reextract: true also re-runs the budget / CTA extraction. Attempts are listed under "retries": [...]
#   "cost" carries the build's total so far, call_detail "Study Build: … (retry)"`;

const WEBHOOK_JSON = `# Delivered when webhook_url was given (retried with back-off until your handler returns 2xx)
POST https://sitecentric.example/hooks/aiservice
Content-Type: application/json
X-AIService-Event: study_build.done            # or study_build.failed · study_build.cancelled
X-AIService-Build-Id: 9698ae46-9dae-4ffe-8b03-35df2a4171a7
X-SiteCentric-Timestamp: 1781779630
X-SiteCentric-Signature: t=1781779630,v1=<hex HMAC-SHA256 of "<timestamp>." + raw body, keyed with your webhook signing secret>

{
  "event": "study_build.done",
  "build_id": "9698ae46-9dae-4ffe-8b03-35df2a4171a7",
  "status": "ready",
  "client_id": "sitecentric",
  "protocol_id": "d1bb0fb6-1676-4707-b3ec-467000559a36",
  "budget_id": "dc72b9aa-9840-4062-bcce-f3b43b48fdad",
  "cta_id": "94ddaf87-b89a-4e70-ad87-0a64ed637ce0",
  "summary": { "protocol_number": "SOL-ADHD-301", "visits": 9, "protocol_visits": 9, "budget_visits": 9,
               "totals": { "grand_total_usd": 28150.22, "visits_total_usd": 15228.22, "start_up_total_usd": 12567.0 },
               "discrepancies": { "total": 20, "high": 1, "medium": 10, "low": 0, "info": 9 }, "matching": "ai" },
  "error": null,
  "result_url": "/api/v1/study-builds/9698ae46-9dae-4ffe-8b03-35df2a4171a7",
  "created_at": "2026-06-18T11:43:02+00:00",
  "finished_at": "2026-06-18T11:47:10+00:00",
  "delivered_at": "2026-06-18T11:47:16+00:00",
  "cost": { "total_cost": 0.2184, "call_detail": "Study Build: Axsome Therapeutics, Inc. SOL-ADHD-301", "call_timestamp": "2026-06-18 11:47" }
}

# Verify (Python) — the signing secret is shown once when the Client Token is provisioned
import hmac, hashlib
ts, v1 = dict(p.split("=", 1) for p in request.headers["X-SiteCentric-Signature"].split(",")).values()
expected = hmac.new(WEBHOOK_SECRET.encode(), f"{ts}.".encode() + raw_body, hashlib.sha256).hexdigest()
assert hmac.compare_digest(expected, v1)
# then GET result_url with the Client Token for the full build JSON`;

const COST_JSON = `# Billing — EVERY successful /api/v1 response ends with a "cost" block (also sent as X-Cost-Total /
# X-Cost-Detail / X-Cost-Timestamp headers, so list-shaped bodies carry it too).
"cost": {
  "total_cost": 0.2184,                                                    // USD — LLM spend + reuse charges, as a total
  "call_detail": "Study Build: Axsome Therapeutics, Inc. SOL-ADHD-301",   // "<Operation>: <sponsor> <protocol>"
  "call_timestamp": "2026-06-18 11:47"                                     // UTC, YYYY-MM-DD HH:MM
}

# What is counted
#  • every LLM round-trip made for the request (extraction, matching, chat, …) at the provider's rate
#  • reuse charges: cached AI results, Data Lake reads and documents a study build reused instead of
#    re-extracting (per-document fee at the tenant's rate; the QA tenant "sitecentric" is exempt → 0)
#  • for asynchronous work the total covers the whole job: a study build = its compose step + the ingest
#    jobs it started (protocol / budget / CTA extraction) + reuse fees; an ingest job = its extraction;
#    a flow run = all its agent steps
#
# How to bill
#  • synchronous calls: total_cost is final in the response
#  • jobs (study builds, ingests, async flows): every poll repeats the job's running total — bill ONCE
#    when the job is terminal (status ready / failed / cancelled, progress.terminal = true); the same
#    block is included in the study_build.done / failed / cancelled webhook
#  • ingest jobs: GET /api/v1/ingest-jobs/{id} stays "processing" (stage: extracting) until the budget /
#    CTA extraction has finished, so its "done" total is final; the record endpoint
#    GET /api/v1/ingest/{protocol|budget|cta}/{id} returns the same total once status is ready
#  • cancel / retry calls return the build's total so far ("Study Build: … (cancel)" / "(retry)")
#  • a retried build keeps its id and its total keeps growing (reused documents are not re-charged,
#    re-run extractions are) — when it is terminal again, bill the difference since the previous terminal
#    total, or simply bill the final total once if you only invoice completed builds`;

const SETUP_JSON = `# v2.18 — study-level setup at the top of "build" (SiteCentric Build Study · Paths · Billing Setup · Study Fees)
# Every setting: { label, value, status, source, quote } — status found · not_found · calculated · default · not_applicable · site_setting.
# A value no document states is null + "not_found" (nothing is guessed). Protocol wins, then budget, then contract
# (disagreements → discrepancy "study_setting_source_conflict" + "conflicts" on the setting).
"study_settings": {
  "max_patients":        { "label": "Max # of Patients", "value": null, "status": "not_found", "source": null, "quote": null },
  "contracted_patients": { "label": "Contracted Patients", "value": null, "status": "not_found", "source": null, "quote": null },
  "additional_patients": { "label": "Add. Patients", "value": null, "status": "not_found", "source": null, "quote": null },
  "initial_amount_per_visit": { "label": "Initial Amount / Visit", "value": 1488.01, "status": "calculated", "source": "budget", "quote": null,
    "calculation": "$13,392.06 per patient (budget 'stated per-patient total') ÷ 9 counted visits on the 'Default' path (unscheduled visit not counted) = $1,488.01" },
  "overhead_percent":    { "label": "Overhead %", "value": 33.0, "status": "found", "source": "budget", "quote": "Overhead: 33%" },
  "withholding_percent": { "label": "Withholding %", "value": 5.0, "status": "found", "source": "budget", "quote": "Holdback: 5%" },
  "round_amounts":       { "label": "Round Amounts", "value": null, "status": "not_found", "source": null, "quote": null },
  "withholding_screen_fails": { "label": "Withholding Screen Fails", "value": null, "status": "not_found", "source": null, "quote": null },
  "visit_timing_mode":   { "label": "Visit Timing Mode", "value": "From Anchor Visit", "status": "default", "source": null, "quote": null },
  "screen_fail_notes":   { "label": "Screen Fail Note(s)", "value": "Screen failures are reimbursed as outlined in the budget (separate payments at Visit 1 and Visit 2).",
                           "status": "found", "source": "contract", "quote": "Screen Failures will be reimbursed, if at all, as outlined in Attachment B." }
},
"screen_fail_handling": {
  "screen_fail_handling": { "label": "Screen Fail Handling", "value": true, "status": "found", "source": "budget",
                            "quote": "SUC061 Screen Failure at Visit 1, as defined in Clinical Trial Agreement — $2,133.44" },
  "sf_payment":     { "label": "SF Payment", "value": "Per Procedure", "status": "found", "source": "budget", "quote": "…" },
  "flat_sf_amount": { "label": "Flat SFs Amount", "value": null, "status": "not_applicable", "source": null, "quote": null },
  "sf_ratio": { "label": "Screen Fails : Enrolled/Randomized", "value": null, "status": "not_found" },   // e.g. "1 : 3"
  "ratio_per": { "value": null }, "pre_ratio_sfs": { "value": null }, "max_sfs_amount": { "value": null }, "invoice_screen_fails": { "value": null }
},
"paths": [
  { "name": "Default", "from": "Triage Workflow", "note": "All three cohorts follow the same visits and procedures …",
    "quote": "Subjects who prematurely discontinue the study will be encouraged to complete Visit 8.", "source": "protocol",
    "visits": ["Screening", "Baseline (Day 1)", "…", "Unscheduled Visit"], "visit_count": 10 }
],                                            // + "visits[].path": "Default" on every visit
"billing_setup": {
  "company": { "label": "Company", "value": "Syneos Health, LLC", "status": "found", "source": "contract", "quote": "Syneos Health, LLC Attn. Investigator Payment Department …" },
  "billing_attn_to": { "value": "Investigator Payment Department" }, "billing_email": { "value": "SM_InvestigatorPayments@Syneoshealth.com" },
  "billing_address_1": { "value": "1030 Sync Street" }, "city": { "value": "Morrisville" }, "state": { "value": "North Carolina" }, "zip": { "value": "27560" },
  "country": { "value": "USA" }, "billing_currency": { "value": "USD", "source": "budget" },
  "net_terms_days": { "value": 45, "quote": "but no later than forty-five (45) calendar days of receipt of an undisputed invoice" },
  "payment_frequency": { "value": "Monthly" },                 // Monthly · Bi-Monthly · Quarterly (other wording → null + quote)
  "billing_phone": { "value": null, "status": "not_found" }, "tax_id": { "value": null, "status": "not_found" }, "…": "…",
  "remit_to_location": { "label": "Remit to Location", "value": null, "status": "site_setting" }   // always empty — your site setting
},
"study_fees": [
  { "invoice_name": "Site Start-Up Fee", "trigger_type": "GREEN LIGHT", "amount": 4000.0, "variable_fee": false, "notification_needed": true,
    "recurrence": false, "recurrence_days": null, "repeat_until_date": null, "apply_overhead": false, "apply_withholding": false,
    "description": "One-time administrative / site start-up fee", "active": true,
    "source": "budget", "quote": "SUC060 Administrative/Site Start Up Costs — $4,000.00 — one-time", "trigger_note": null },
  { "invoice_name": "Dry Ice (pass-through)", "trigger_type": "DRY ICE USE", "amount": null, "variable_fee": true, "notification_needed": false, "…": "…" },
  { "invoice_name": "Pharmacy Maintenance Fee", "trigger_type": "FPV", "recurrence": true, "recurrence_days": 365, "repeat_until_date": "2041-10-29", "…": "…" }
],                                            // only FPV / SIV fees recur; Repeat Until = build date + 15 years
"about_study": {
  "title": "Study Knowledgebase",
  "html": "<h3>Contract Info</h3><ul><li><strong>Number of patients:</strong> Competitive enrollment</li>…</ul><h3>Patient Tracking</h3><ul><li><strong>Screening</strong> – All procedures completed OR Screen Fail</li>…</ul><p><strong>Patient stipend</strong> · <strong>Card holder relationship:</strong> SELF</p><h3>Additional Information</h3><ul><li>This study has a reconsent fee.</li></ul>",
  "contract_info": [{ "topic": "number_of_patients", "label": "Number of patients", "text": "Competitive enrollment" }, "…"],
  "patient_tracking": [{ "visit": "Screening", "path": "Default", "rule": "All procedures completed OR Screen Fail" }, "…"],
  "stipend": { "vendor": null, "card_holder_relationship": "SELF" },          // GUARDIAN when minors are enrolled
  "additional_information": ["This study has a reconsent fee."],
  "age_range": { "min": 18, "max": 55 }, "minors_enrolled": false, "site_amounts_removed": 0   // never shows what the site is paid
},
"setup_info": { "method": "ai", "calls": { "settings": { "model": "claude-sonnet-5-5", "duration_ms": 59963 }, "fees_article": { "…": "…" }, "paths": { "…": "…" } },
                "sources_read": ["protocol", "budget", "contract"], "conflicts": [], "path_fixes": [], "fees_left_out": [], "notes": ["…"] }`;

const TABS = [
    { id: "upload", label: "1 · Upload files (curl)", snippet: UPLOAD_CURL },
    { id: "json", label: "1b · Stored ids (JSON)", snippet: JSON_CURL },
    { id: "poll", label: "2 · 202 + poll", snippet: ACCEPTED_JSON },
    { id: "result", label: "3 · Result JSON", snippet: RESULT_JSON },
    { id: "setup", label: "Study setup (v2.18)", snippet: SETUP_JSON },
    { id: "webhook", label: "Webhook payload", snippet: WEBHOOK_JSON },
    { id: "cost", label: "Cost block (billing)", snippet: COST_JSON },
];

export function StudyBuildRecipe() {
    const [tab, setTab] = useState("upload");
    const active = TABS.find((t) => t.id === tab) || TABS[0];
    const copy = async (text, label) => {
        try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
        catch { toast.error("Copy was blocked by the browser"); }
    };
    return (
        <section id="study-build-recipe" data-testid="study-build-recipe-section" className="mb-10">
            <div className="mb-3">
                <h3 className="font-display text-xl tracking-tight flex items-center gap-2">
                    <Layers className="w-5 h-5 text-emerald-300" /> Study build · files or Data Lake ids → priced schedule
                </h3>
                <p className="text-sm text-white/50 mt-1 max-w-3xl">
                    One call with the Client Token builds the whole study: protocol Schedule of Events (cohort-aware,
                    no duplicated visits) priced against the budget, CTA payment terms, and every protocol-vs-budget
                    disagreement listed under <code className="px-1 bg-white/5 rounded">discrepancies[]</code>. Send
                    new files, reference stored documents, or mix both — then poll or receive the signed webhook.
                </p>
            </div>
            <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.04] p-5">
                <div className="text-[11px] uppercase tracking-wider text-emerald-200/80 mb-3">
                    Submit → 202 → poll (or webhook) → full JSON
                </div>
                <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="study-build-recipe-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`study-build-recipe-tab-${t.id}`}
                            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${tab === t.id
                                ? "border-emerald-400/50 bg-emerald-400/15 text-emerald-100"
                                : "border-white/10 text-white/55 hover:border-white/20"}`}
                        >{t.label}</button>
                    ))}
                </div>
                <div className="relative">
                    <pre
                        className="text-[11px] bg-black/40 border border-white/10 rounded px-3 py-3 overflow-auto font-mono leading-relaxed text-emerald-50 whitespace-pre max-h-[560px]"
                        data-testid={`study-build-recipe-snippet-${active.id}`}
                    >{active.snippet}</pre>
                    <button
                        onClick={() => copy(active.snippet, active.label)}
                        data-testid={`study-build-recipe-copy-${active.id}`}
                        className="absolute top-2 right-2 text-[10px] text-white/40 hover:text-white inline-flex items-center gap-1 bg-black/50 px-1.5 py-0.5 rounded"
                    ><Copy className="w-3 h-3" /> copy</button>
                </div>
                <div className="mt-3 text-[11px] text-white/50 leading-relaxed">
                    <code className="px-1 bg-white/5 rounded">protocol</code> and{" "}
                    <code className="px-1 bg-white/5 rounded">budget</code> are required (file or id), the CTA is optional.
                    Ids are tenant-scoped to the Client Token. The build never fails on disagreements — it completes with{" "}
                    <code className="px-1 bg-white/5 rounded">status: ready</code> and lists them with a severity; only a
                    failed extraction yields <code className="px-1 bg-white/5 rounded">status: failed</code>. Every response ends
                    with <code className="px-1 bg-white/5 rounded">cost</code> — the USD total (LLM spend + reuse charges) of the
                    request and the job it reports on, with <code className="px-1 bg-white/5 rounded">call_detail</code> and{" "}
                    <code className="px-1 bg-white/5 rounded">call_timestamp</code> — see the Cost block tab.
                </div>
            </div>
        </section>
    );
}
